/**
 * Avatar-facing copy for the interactive flywheel.
 *
 * VOICE RULE (customer-facing): talk to the buyer in their language. Second person,
 * concrete, honest. NO internal labels (no "Builder", "doctrine", "Door 1/2",
 * "Lean Launch", "Model-Upgrade Dividend"). NO em dashes, no "It's not X it's Y",
 * no colon reveals, no "from X to Y", no AI-slop vocab. Internal doctrine names stay
 * in the design doc, never on screen.
 */

export type StageKey = 'capture' | 'assess' | 'build' | 'launch' | 'improve';

export interface Stage {
  key: StageKey;
  /** Short loop-stage name shown as a small tag (the public flywheel stage). */
  tag: string;
  /** Plain-English, benefit-led title. */
  title: string;
  /** What it does, in their words. */
  body: string;
  /** A concrete "this is you" example. */
  forYou: string;
}

export const HEADER = {
  eyebrow: 'Your growth flywheel',
  title: 'Build a business that gets better as AI gets smarter.',
  sub: 'Most people use AI from a cold start every time. Put one at the center of your business with full context, and it sharpens every decision, shows you the bottlenecks, and compounds your growth.',
} as const;

export const INTRO = {
  oldWayTag: 'The old way',
  oldWayLine: 'You use AI every day, but it forgets your business the moment you close the tab. Every answer starts from a cold prompt, and nothing it learns sticks.',
  newWayTag: 'The new way',
  newWayLine: 'What if a genius-level AI sat at the center of your business, knew the whole thing, and got sharper every week?',
  cue: 'Press start',
} as const;

export const BRAIN = {
  tag: 'Your Business Brain',
  title: 'The intelligence layer at the center of your business',
  body: 'It holds your full context, your offer, your positioning, your IP, and the live data from everything you run. Because it knows the whole picture, it runs the loop on every part of your business.',
  forYou: 'A brain that already knows your business, so you stop starting from a cold prompt, and it gets sharper with every result.',
} as const;

export const STAGES: Stage[] = [
  {
    key: 'capture',
    tag: 'Capture',
    title: 'Give the Brain the full picture',
    body: 'Your offer, your positioning, your IP, and the live results from everything you run, all in one place the Brain can use. The more it holds, the smarter every move gets.',
    forYou: 'It learns your business once, then keeps learning from every campaign, call, and customer.',
  },
  {
    key: 'assess',
    tag: 'Assess',
    title: 'The Brain decides what to improve next',
    body: 'It reads your full context and the latest data, finds the bottleneck, and picks the highest-leverage thing to test. A landing page, a sales script, an ad, an email, an automation, wherever the next gain is.',
    forYou: 'No more guessing what to work on. The Brain points at the move that actually shifts the number.',
  },
  {
    key: 'build',
    tag: 'Build',
    title: 'It builds the asset to test',
    body: 'A landing page, a sales script, an ad, an email sequence, an SOP, an automation. The Brain creates it in your voice, ready to ship, in minutes instead of weeks.',
    forYou: 'The thing you would have spent a week making, drafted and ready, sounding like you.',
  },
  {
    key: 'launch',
    tag: 'Launch',
    title: 'It goes live to collect real data',
    body: 'The asset ships into your market and starts gathering signal: what converts, what gets ignored, where people drop off. Real behaviour, not opinions.',
    forYou: 'Every page, ad, and message in market becomes a source of truth instead of a guess.',
  },
  {
    key: 'improve',
    tag: 'Improve',
    title: 'The data flows back, and it compounds',
    body: 'The results return to the Brain. It learns what worked, updates your context, and sets the next test, on its own and around the clock. Every loop leaves the business a little smarter than the last.',
    forYou: 'Nothing you run is wasted. Win or lose, it makes the next move sharper.',
  },
];

export const HINT = 'Tap a step to explore it. Run a lap to watch it compound.';

export const COMPOUNDING = {
  runLabel: 'Run a lap',
  runningLabel: 'Running',
  compoundsLabel: 'Effort that compounds',
  resetsLabel: 'Effort that resets',
  lapsLabel: 'Laps run',
  brainLabel: 'Your Brain',
  caption: 'Run it again. And again. The Brain tests, learns, and tunes every part of your business in the background, so the results keep climbing while the work gets lighter.',
} as const;

export const MODEL_UPGRADE = {
  button: 'A smarter AI just shipped',
  effect: 'Because you own your context, every upgrade makes your whole business better overnight. You do not start over. You pick up where you were, only stronger.',
} as const;

export const CTA = {
  line: 'This is the business you have been trying to build.',
  button: 'Build yours',
  href: '#',
} as const;
